/**
 * AccessMap Constants
 * 
 * Centralized constants and enums for access map operations.
 * This file provides a single source of truth for all access map-related constants.
 */

// ============================================================================
// ROLE TYPES
// ============================================================================

/**
 * User role types in the access map system
 * Team hierarchy: OWNER > CONTRIBUTOR > VIEWER
 * Public-policy-only roles: DEFAULT (inherit), NONE (protected)
 */
export const ROLE_TYPES = {
  OWNER: 'OWNER',
  CONTRIBUTOR: 'CONTRIBUTOR',
  VIEWER: 'VIEWER',
  /** Public access policy only — inherit from parent */
  DEFAULT: 'DEFAULT',
  /** Public access policy only — explicitly private / protected */
  NONE: 'NONE',
};

/**
 * Array of role types in hierarchical order (highest to lowest)
 */
export const ROLE_HIERARCHY = [
  ROLE_TYPES.OWNER,
  ROLE_TYPES.CONTRIBUTOR,
  ROLE_TYPES.VIEWER
];

/**
 * Role type labels for UI display
 */
export const ROLE_LABELS = {
  [ROLE_TYPES.OWNER]: 'Owner Level',
  [ROLE_TYPES.CONTRIBUTOR]: 'Contributor Level',
  [ROLE_TYPES.VIEWER]: 'Viewer Level'
};

/**
 * Role type descriptions for UI tooltips/help text
 */
export const ROLE_DESCRIPTIONS = {
  [ROLE_TYPES.OWNER]: 'Full control: create, edit, delete, and manage settings',
  [ROLE_TYPES.CONTRIBUTOR]: 'Can edit content but cannot delete or change settings',
  [ROLE_TYPES.VIEWER]: 'Can only view, no editing permissions'
};

// ============================================================================
// ACCESS TYPES
// ============================================================================

/**
 * Access types for role access lists
 */
export const ACCESS_TYPES = {
  PUBLIC: 'PUBLIC',
  RESTRICTED: 'RESTRICTED'
};

// ============================================================================
// TAB NAMES
// ============================================================================

/**
 * Tab names for filtering items by access level
 */
export const TAB_NAMES = {
  MY_PROJECTS: 'myProjects',
  MY_COLLECTIONS: 'myCollections',
  MY_ITEMS: 'myItems',
  /** Discovery tab (public items where user has no explicit role) - used for both projects and collections */
  DISCOVERY: 'discovery'
};

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/**
 * Default roleAccessList structure (all roles restricted)
 */
export const DEFAULT_ROLE_ACCESS_LIST = {
  [ROLE_TYPES.OWNER]: ACCESS_TYPES.RESTRICTED,
  [ROLE_TYPES.CONTRIBUTOR]: ACCESS_TYPES.RESTRICTED,
  [ROLE_TYPES.VIEWER]: ACCESS_TYPES.RESTRICTED
};

/**
 * Public roleAccessList structure (CONTRIBUTOR and VIEWER public, OWNER restricted)
 * Note: OWNER is always RESTRICTED for public access
 */
export const PUBLIC_ROLE_ACCESS_LIST = {
  [ROLE_TYPES.OWNER]: ACCESS_TYPES.RESTRICTED,
  [ROLE_TYPES.CONTRIBUTOR]: ACCESS_TYPES.PUBLIC,
  [ROLE_TYPES.VIEWER]: ACCESS_TYPES.PUBLIC
};

// ============================================================================
// PUBLIC ACCESS OPTIONS (Radio Button Options)
// ============================================================================

/**
 * UI public access option keys (screen labels: Default / Protected / View / Edit).
 * Protected maps to backend publicAccessPolicy role NONE.
 */
export const PUBLIC_ACCESS_OPTIONS = {
  DEFAULT: 'default',
  PROTECTED: 'protected',
  /** @deprecated Use PROTECTED — kept for older filter/card values */
  NONE: 'none',
  VIEW: 'view',
  EDIT: 'edit'
};

/**
 * Backend public-access encoding.
 * Current BE: publicAccessPolicy RoleType values (DEFAULT | NONE | VIEWER | CONTRIBUTOR).
 */
export const PUBLIC_ACCESS_POLICY_BY_OPTION = {
  [PUBLIC_ACCESS_OPTIONS.DEFAULT]: [ROLE_TYPES.DEFAULT],
  [PUBLIC_ACCESS_OPTIONS.PROTECTED]: [ROLE_TYPES.NONE],
  [PUBLIC_ACCESS_OPTIONS.NONE]: [ROLE_TYPES.NONE],
  [PUBLIC_ACCESS_OPTIONS.VIEW]: [ROLE_TYPES.VIEWER],
  [PUBLIC_ACCESS_OPTIONS.EDIT]: [ROLE_TYPES.CONTRIBUTOR],
};

/**
 * Public access option labels for UI display
 */
export const PUBLIC_ACCESS_LABELS = {
  [PUBLIC_ACCESS_OPTIONS.DEFAULT]: 'Default',
  [PUBLIC_ACCESS_OPTIONS.PROTECTED]: 'Protected',
  [PUBLIC_ACCESS_OPTIONS.NONE]: 'Protected',
  [PUBLIC_ACCESS_OPTIONS.VIEW]: 'View',
  [PUBLIC_ACCESS_OPTIONS.EDIT]: 'Edit'
};

/**
 * Public access option descriptions for UI tooltips/help text
 */
export const PUBLIC_ACCESS_DESCRIPTIONS = {
  [PUBLIC_ACCESS_OPTIONS.DEFAULT]:
    'Inherit public access from the parent. If nothing in the chain is public, this item stays private.',
  [PUBLIC_ACCESS_OPTIONS.PROTECTED]:
    'Explicitly private. Only assigned users can access it, even if a parent is public.',
  [PUBLIC_ACCESS_OPTIONS.NONE]:
    'Explicitly private. Only assigned users can access it, even if a parent is public.',
  [PUBLIC_ACCESS_OPTIONS.VIEW]:
    'Everyone can see this item, but only assigned users can edit it.',
  [PUBLIC_ACCESS_OPTIONS.EDIT]:
    'Everyone can see and edit this item.'
};

/**
 * Public access configuration array for radio buttons
 * Used in PublicAccessField component
 */
export const PUBLIC_ACCESS_CONFIG = [
  {
    key: PUBLIC_ACCESS_OPTIONS.DEFAULT,
    label: PUBLIC_ACCESS_LABELS[PUBLIC_ACCESS_OPTIONS.DEFAULT],
    description: PUBLIC_ACCESS_DESCRIPTIONS[PUBLIC_ACCESS_OPTIONS.DEFAULT]
  },
  {
    key: PUBLIC_ACCESS_OPTIONS.PROTECTED,
    label: PUBLIC_ACCESS_LABELS[PUBLIC_ACCESS_OPTIONS.PROTECTED],
    description: PUBLIC_ACCESS_DESCRIPTIONS[PUBLIC_ACCESS_OPTIONS.PROTECTED]
  },
  {
    key: PUBLIC_ACCESS_OPTIONS.VIEW,
    label: PUBLIC_ACCESS_LABELS[PUBLIC_ACCESS_OPTIONS.VIEW],
    description: PUBLIC_ACCESS_DESCRIPTIONS[PUBLIC_ACCESS_OPTIONS.VIEW]
  },
  {
    key: PUBLIC_ACCESS_OPTIONS.EDIT,
    label: PUBLIC_ACCESS_LABELS[PUBLIC_ACCESS_OPTIONS.EDIT],
    description: PUBLIC_ACCESS_DESCRIPTIONS[PUBLIC_ACCESS_OPTIONS.EDIT]
  }
];

/**
 * Map UI option → backend publicAccessPolicy RoleType list.
 * Default always writes [DEFAULT] (not empty).
 */
export const uiOptionToPublicAccessPolicy = (option) => {
  const resolved =
    option === PUBLIC_ACCESS_OPTIONS.NONE
      ? PUBLIC_ACCESS_OPTIONS.PROTECTED
      : option;
  return (
    PUBLIC_ACCESS_POLICY_BY_OPTION[resolved] ||
    PUBLIC_ACCESS_POLICY_BY_OPTION[PUBLIC_ACCESS_OPTIONS.PROTECTED]
  ).slice();
};

/**
 * Map a single policy/role token → UI option.
 * Accepts DEFAULT | NONE | VIEWER | CONTRIBUTOR (and legacy VIEW/EDIT aliases).
 */
export const publicAccessTokenToUiOption = (token) => {
  const normalized = String(token || '').trim().toUpperCase();
  switch (normalized) {
    case ROLE_TYPES.DEFAULT:
      return PUBLIC_ACCESS_OPTIONS.DEFAULT;
    case ROLE_TYPES.VIEWER:
    case 'VIEW':
      return PUBLIC_ACCESS_OPTIONS.VIEW;
    case ROLE_TYPES.CONTRIBUTOR:
    case 'EDIT':
      return PUBLIC_ACCESS_OPTIONS.EDIT;
    case ROLE_TYPES.NONE:
      return PUBLIC_ACCESS_OPTIONS.PROTECTED;
    default:
      return null;
  }
};

/**
 * Map backend publicAccessPolicy → UI option.
 * Precedence matches BE getEffectivePublicRole: CONTRIBUTOR > VIEWER > NONE > DEFAULT.
 * Empty / missing policy → Default (legacy records).
 */
export const publicAccessPolicyToUiOption = (publicAccessPolicy) => {
  if (!publicAccessPolicy || !Array.isArray(publicAccessPolicy) || publicAccessPolicy.length === 0) {
    return PUBLIC_ACCESS_OPTIONS.DEFAULT;
  }
  const set = new Set(
    publicAccessPolicy.map((role) => String(role || '').trim().toUpperCase()).filter(Boolean)
  );
  if (set.has(ROLE_TYPES.CONTRIBUTOR)) {
    return PUBLIC_ACCESS_OPTIONS.EDIT;
  }
  if (set.has(ROLE_TYPES.VIEWER)) {
    return PUBLIC_ACCESS_OPTIONS.VIEW;
  }
  if (set.has(ROLE_TYPES.NONE)) {
    return PUBLIC_ACCESS_OPTIONS.PROTECTED;
  }
  if (set.has(ROLE_TYPES.DEFAULT)) {
    return PUBLIC_ACCESS_OPTIONS.DEFAULT;
  }
  return PUBLIC_ACCESS_OPTIONS.DEFAULT;
};

// ============================================================================
// ROLE CONFIGURATION (Legacy - kept for team member roles)
// ============================================================================

/**
 * Role configuration array for UI components
 * Used for team member roles (not public access)
 */
export const ROLE_CONFIG = [
  {
    key: ROLE_TYPES.OWNER,
    label: ROLE_LABELS[ROLE_TYPES.OWNER],
    description: ROLE_DESCRIPTIONS[ROLE_TYPES.OWNER]
  },
  {
    key: ROLE_TYPES.CONTRIBUTOR,
    label: ROLE_LABELS[ROLE_TYPES.CONTRIBUTOR],
    description: ROLE_DESCRIPTIONS[ROLE_TYPES.CONTRIBUTOR]
  },
  {
    key: ROLE_TYPES.VIEWER,
    label: ROLE_LABELS[ROLE_TYPES.VIEWER],
    description: ROLE_DESCRIPTIONS[ROLE_TYPES.VIEWER]
  }
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Gets the index of a role in the hierarchy (0 = highest, 2 = lowest)
 * @param {string} roleType - The role type
 * @returns {number} - Index in hierarchy, or -1 if not found
 */
export const getRoleHierarchyIndex = (roleType) => {
  return ROLE_HIERARCHY.indexOf(roleType);
};

/**
 * Checks if role1 is higher in hierarchy than role2
 * @param {string} role1 - First role type
 * @param {string} role2 - Second role type
 * @returns {boolean} - True if role1 is higher than role2
 */
export const isRoleHigher = (role1, role2) => {
  const index1 = getRoleHierarchyIndex(role1);
  const index2 = getRoleHierarchyIndex(role2);
  return index1 !== -1 && index2 !== -1 && index1 < index2;
};

/**
 * Gets all roles that are lower in hierarchy than the given role
 * @param {string} roleType - The role type
 * @returns {Array<string>} - Array of lower roles
 */
export const getLowerRoles = (roleType) => {
  const index = getRoleHierarchyIndex(roleType);
  if (index === -1) return [];
  return ROLE_HIERARCHY.slice(index + 1);
};

/**
 * Gets all roles that are higher in hierarchy than the given role
 * @param {string} roleType - The role type
 * @returns {Array<string>} - Array of higher roles
 */
export const getHigherRoles = (roleType) => {
  const index = getRoleHierarchyIndex(roleType);
  if (index === -1) return [];
  return ROLE_HIERARCHY.slice(0, index);
};

